import { useEffect, useState } from "react";
import { ShieldCheck, LogOut, Users, Search, GripVertical, Plus, History, FolderTree, UserCircle, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { SortableGrid } from "@/components/SortableGrid";
import { SiteTile } from "@/components/SiteTile";
import { EditSiteDialog } from "@/components/EditSiteDialog";
import { UsersDialog } from "@/components/UsersDialog";
import { GroupsDialog } from "@/components/GroupsDialog";
import { AccessLogDialog } from "@/components/AccessLogDialog";
import { ProfileDialog } from "@/components/ProfileDialog";

const NEW_SITE = { title: "", url: "", description: "", image_url: "", group_id: null };
const headerBtn = "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white";

function HeaderButton({ icon: Icon, label, onClick, testId }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick} data-testid={testId} className={headerBtn} title={label}>
      <Icon className="w-4 h-4 lg:mr-1.5" /><span className="hidden lg:inline">{label}</span>
    </Button>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [sites, setSites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialog, setDialog] = useState(null);
  const isAdmin = user.role === "admin";

  useEffect(() => {
    Promise.all([api.get("/sites"), api.get("/groups")])
      .then(([s, g]) => { setSites(s.data); setGroups(g.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  const reorder = async (next) => {
    const prev = sites;
    setSites(next.map((s, i) => ({ ...s, position: i + 1 })));
    try {
      const { data } = await api.post("/sites/reorder", { ids: next.map((s) => s.id) });
      setSites(data);
      toast.success("Ordine salvato");
    } catch (e) {
      setSites(prev);
      toast.error(errMsg(e));
    }
  };

  const onSaved = (s) =>
    setSites((prev) => (prev.some((x) => x.id === s.id) ? prev.map((x) => (x.id === s.id ? s : x)) : [...prev, s]));
  const q = query.toLowerCase();
  const visible = sites.filter((s) => `${s.title} ${s.url} ${s.description}`.toLowerCase().includes(q));
  const groupIds = new Set(groups.map((g) => g.id));
  const sections = [
    ...groups.map((g) => ({ key: g.id, name: g.name, sites: visible.filter((s) => s.group_id === g.id) })),
    { key: "none", name: groups.length ? "Senza sezione" : null, sites: visible.filter((s) => !groupIds.has(s.group_id)) },
  ].filter((sec) => sec.sites.length || (isAdmin && !query && sec.key !== "none"));

  const reorderSection = (key, list) =>
    reorder(sections.flatMap((sec) => (sec.key === key ? list : sec.sites)));

  const onGroupDeleted = (gid) => setSites((l) => l.map((s) => (s.group_id === gid ? { ...s, group_id: null } : s)));
  const favIds = user.favorites || [];
  const favorites = visible.filter((s) => favIds.includes(s.id));

  return (
    <div className="min-h-screen grid-bg noise">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0F17]/80 border-b border-slate-800/80">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
          <div className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-slate-300" data-testid="dashboard-header-title">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> TrustMaster
          </div>
          <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Accesso protetto
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setDialog("profile")} data-testid="user-profile-badge" title="Il mio profilo"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-2 py-1 rounded-md hover:bg-slate-800 transition-colors">
              <UserCircle className="w-4 h-4" />
              <span className="hidden sm:inline">{user.name}</span>
              <span className="hidden sm:inline font-mono text-[10px] uppercase text-slate-500">{user.role}</span>
            </button>
            {isAdmin && (
              <>
                <HeaderButton icon={FolderTree} label="Sezioni" onClick={() => setDialog("groups")} testId="admin-groups-button" />
                <HeaderButton icon={History} label="Accessi" onClick={() => setDialog("logs")} testId="admin-access-log-button" />
                <HeaderButton icon={Users} label="Utenti" onClick={() => setDialog("users")} testId="admin-users-button" />
              </>
            )}
            <Button size="sm" variant="ghost" onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-white hover:bg-slate-800">
              <LogOut className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-8 py-10 sm:py-14">
        <div className="flex flex-col md:flex-row md:items-end gap-6 mb-10 rise">
          <div>
            <span className="font-mono text-[11px] text-blue-400 tracking-[0.2em] uppercase">Ciao {user.name}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2">I tuoi siti</h1>
            <p className="font-mono text-xs text-slate-500 mt-3" data-testid="sites-counter">{visible.length} / {sites.length} siti</p>
          </div>
          <div className="flex gap-2 md:ml-auto">
            <div className="relative md:w-72 flex-1">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca un sito…" data-testid="sites-search-input"
                className="pl-10 h-10 bg-slate-900/70 border-slate-700 text-slate-100" />
            </div>
            {isAdmin && (
              <Button onClick={() => setEditing(NEW_SITE)} data-testid="add-site-button" className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Aggiungi sito</span>
              </Button>
            )}
          </div>
        </div>

        {isAdmin && (
          <p className="font-mono text-[11px] text-slate-500 mb-4 flex items-center gap-1.5" data-testid="reorder-hint">
            <GripVertical className="w-3.5 h-3.5" />
            {query ? "Svuota la ricerca per riordinare i riquadri" : "Trascina la maniglia di un riquadro per cambiarne la posizione"}
          </p>
        )}
        <div className="space-y-12">
          {favorites.length > 0 && (
            <section data-testid="section-favorites">
              <div className="flex items-baseline gap-3 mb-4 border-b border-amber-500/20 pb-2">
                <h2 className="font-display text-lg font-bold tracking-tight text-amber-300 flex items-center gap-2" data-testid="section-title-favorites">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> I miei preferiti
                </h2>
                <span className="font-mono text-[11px] text-slate-500">{favorites.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-testid="sites-grid-favorites">
                {favorites.map((s, i) => (
                  <SiteTile key={s.id} site={s} index={i} isAdmin={isAdmin} onEdit={() => setEditing(s)} tp="fav-tile" />
                ))}
              </div>
            </section>
          )}
          {sections.map((sec) => (
            <section key={sec.key} data-testid={`section-${sec.key}`}>
              {sec.name && (
                <div className="flex items-baseline gap-3 mb-4 border-b border-slate-800/80 pb-2">
                  <h2 className="font-display text-lg font-bold tracking-tight text-slate-100" data-testid={`section-title-${sec.key}`}>{sec.name}</h2>
                  <span className="font-mono text-[11px] text-slate-500">{sec.sites.length}</span>
                </div>
              )}
              {sec.sites.length ? (
                <SortableGrid sites={sec.sites} isAdmin={isAdmin} disabled={!!query} onEdit={setEditing}
                  onReorder={(list) => reorderSection(sec.key, list)} testId={`sites-grid-${sec.key}`} />
              ) : (
                <p className="text-xs text-slate-500 font-mono" data-testid={`section-empty-${sec.key}`}>Sezione vuota: assegna un sito da “Modifica”.</p>
              )}
            </section>
          ))}
        </div>
      </main>

      <EditSiteDialog site={editing} groups={groups} onClose={() => setEditing(null)} onSaved={onSaved} onDeleted={setSites} />
      <ProfileDialog open={dialog === "profile"} onOpenChange={(o) => setDialog(o ? "profile" : null)} user={user} />
      {isAdmin && (
        <>
          <UsersDialog open={dialog === "users"} onOpenChange={(o) => setDialog(o ? "users" : null)} currentUserId={user.id} />
          <AccessLogDialog open={dialog === "logs"} onOpenChange={(o) => setDialog(o ? "logs" : null)} />
          <GroupsDialog open={dialog === "groups"} onOpenChange={(o) => setDialog(o ? "groups" : null)}
            groups={groups} setGroups={setGroups} onGroupDeleted={onGroupDeleted} />
        </>
      )}
    </div>
  );
}
